import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ExternalLink, FileText, Image as ImageIcon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { expensesApi } from "@/lib/api";
import ChartViewer from "./ChartViewer";

// Lightbox for viewing an uploaded receipt without leaving the app.
//
// Design decisions:
//  - Radix Dialog gives us the modal chrome + close X + focus trap.
//  - The inner viewer is now ChartViewer, which handles pinch-to-zoom,
//    two-finger pan, and 90° rotation for both raster images and PDFs on
//    iOS Safari (and mouse/trackpad on desktop via Ctrl+wheel).
//  - Mints a 5-min HMAC-signed URL each time it opens so the browser can
//    fetch the receipt without an auth header. Regenerated on every open
//    so expiry never affects the current view.
//  - "Open in new tab" reuses the same signed URL so users can print or
//    download from the browser's native viewer if needed.
export function ReceiptLightbox({ open, onOpenChange, projectId, expense }) {
  const [signedUrl, setSignedUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const filename = expense?.receipt_filename || "receipt";
  const contentType = expense?.receipt_content_type || "";
  const isPdf =
    contentType === "application/pdf" || filename.toLowerCase().endsWith(".pdf");
  const isImage =
    contentType.startsWith("image/") ||
    /\.(jpe?g|png|webp|gif)$/i.test(filename);
  const mime = isPdf ? "application/pdf" : contentType || "image/*";

  useEffect(() => {
    if (!open || !expense) return;
    // Bypass path — the caller (e.g. Idea attachment) already handed
    // us a fully-formed presigned URL; skip the receipt endpoint.
    if (expense._presignedUrl) {
      setSignedUrl(expense._presignedUrl);
      setLoading(false);
      setErr("");
      return;
    }
    let cancelled = false;
    setLoading(true);
    setErr("");
    setSignedUrl("");
    (async () => {
      try {
        const res = await expensesApi.receiptSignedUrl(projectId, expense.id);
        if (!cancelled) setSignedUrl(`${window.location.origin}${res.url}`);
      } catch (e) {
        if (!cancelled) {
          const msg = e?.response?.data?.detail || "Could not load receipt";
          setErr(msg);
          toast.error(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, expense, projectId]);

  const handleOpenNewTab = () => {
    if (!signedUrl) return;
    window.open(signedUrl, "_blank", "noopener,noreferrer");
  };

  if (!expense) return null;
  const TypeIcon = isPdf ? FileText : ImageIcon;
  const canRender = isPdf || isImage;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="receipt-lightbox"
        className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-5xl w-[95vw] h-[92vh] p-0 flex flex-col gap-0"
      >
        <DialogHeader className="px-5 pt-4 pb-3 border-b border-zinc-800 flex-row items-center justify-between space-y-0">
          <DialogTitle className="flex items-center gap-2 text-base font-medium min-w-0">
            <TypeIcon className="w-4 h-4 text-amber-500 shrink-0" />
            <span className="truncate" title={filename}>
              {filename}
            </span>
            {expense.merchant && (
              <span className="text-xs text-zinc-500 truncate ml-1">
                · {expense.merchant}
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {isPdf ? "PDF receipt preview" : "Image receipt preview"} for{" "}
            {filename}. Pinch to zoom, drag to pan, or use Open in New Tab to
            print or download.
          </DialogDescription>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="receipt-lightbox-open-new-tab"
              onClick={handleOpenNewTab}
              disabled={!signedUrl}
              className="bg-transparent border-zinc-800 text-zinc-300 hover:bg-zinc-900 rounded-sm h-8 mr-6"
            >
              <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Open in new tab
            </Button>
            {/* Radix Dialog auto-renders the X close button in the top-right. */}
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 bg-zinc-900/40 relative overflow-hidden">
          {loading && (
            <div
              data-testid="receipt-lightbox-loading"
              className="absolute inset-0 flex items-center justify-center"
            >
              <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
            </div>
          )}
          {err && !loading && (
            <div className="absolute inset-0 flex items-center justify-center text-sm text-rose-400 px-6 text-center">
              {err}
            </div>
          )}
          {signedUrl && !err && !loading && (
            canRender ? (
              <ChartViewer
                src={signedUrl}
                mime={mime}
                accentColor="#F59E0B"
              />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-zinc-500 text-sm px-6 text-center">
                <FileText className="w-8 h-8" />
                <div>Can't preview this file type inline.</div>
                <Button
                  onClick={handleOpenNewTab}
                  className="bg-amber-500 text-black hover:bg-amber-400"
                >
                  <ExternalLink className="w-4 h-4 mr-2" /> Open in new tab
                </Button>
              </div>
            )
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
